import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  CheckIcon,
  ArrowRightIcon,
  XCircleIcon,
  CaretDownIcon
} from "@phosphor-icons/react";
import { useDispatch, useSelector } from "react-redux";

import Icon from "components/Icon/Icon";
import EditCampaign from "pages/Marketplace/Campaign/EditCampaign";
import Button from "components/Buttons";

import { CampaignContainer } from "./styled";
import { CompaniesWrapperStyles } from "pages/Companies/styled";

import {
  selectCreateMarketplaceCampaign,
  selectMarketplace,
  selectMarketplaceUser,
  selectSelectedMarketplace
} from "../slice/selector";
import { useMarketplaceSlice } from "../slice";
import LoadingSpinner from "components/PageLoader/Spinner";
import UserDetails from "./CreateCampaign/UserDetails";
import RequestDetails from "./CreateCampaign/RequestDetails";

import {
  COLLABORATION_TYPE,
  MARKETPLACE_APPROVE_OPTIONS,
  MARKETPLACE_REJECT_OPTIONS,
  rejectReasonOptions,
  selectOptionsMap
} from "./data";

import { useValidationSlice } from "store/validation/slice/index";
import { triggerEmailValidation } from "store/validation/slice/selector";
import {
  selectCategories,
  selectCities,
  selectLanguages,
  selectCompanies
} from "store/dropdownOptions/slice/selector";
import { useDropdownSlice } from "store/dropdownOptions/slice/index";
import PillButtons from "components/Buttons/PillButtons";

/* ---------- Universal validator ---------- */
const isValidField = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return true;
  if (typeof value === "boolean") return true;
  if (Array.isArray(value)) return value.length > 0;
  if (value instanceof File) return true;
  if (typeof value === "object") {
    if ("value" in value) return value.value !== null;
    return Object.keys(value).length > 0;
  }
  return false;
};

/* ---------- Payload key mapping ---------- */
const payloadKeyMap = {
  email: "email",
  first_name: "first_name",
  user_id_hidden: "user_id_hidden",
  last_name: "last_name",
  linkedin_url: "linkedin_url",
  user_phone_number: "user_phone_number",
  user_designation: "user_designation",
  user_profile_image: "user_profile_image",
  company: "company_id",
  service_type: "service_type",
  platform: "platform",
  categories_selector: "categories_selector",
  city_selector: "city_selector",
  is_pan_india: "is_pan_india",
  language_selector: "language_selector",
  any_language: "any_language",
  is_high_value: "is_high_value",
  description: "description",
  brand_info: "brand_info",
  brand: "brand",
  creator_count_min: "creator_count_min",
  creator_count_max: "creator_count_max",
  barter: "barter",
  budget_type: "budget_type",
  budget: "budget",
  budget_min: "budget_min",
  budget_max: "budget_max",
  creator_range_selector: "creator_range_selector",
  status: "status",
  target_audience: "target_audience"
};

const USER_REQUIRED_KEYS = [
  "first_name",
  "last_name",
  "email",
  "linkedin_url",
  "user_phone_number",
  "user_designation",
  // "company",
  "user_profile_image"
];

const REQUEST_REQUIRED_KEYS = [
  "service_type",
  "description",
  "status",
  "target_audience"
];

const getEmptyUserDetails = () => ({
  user_id_hidden: -1,
  first_name: "",
  last_name: "",
  email: "",
  linkedin_url: "",
  user_phone_number: "",
  user_designation: "",
  company: null,
  user_profile_image: null
});

/* ---------- Transform state to backend payload ---------- */
const transformPayload = (userDetails, requestDetails) => {
  const payload = {};

  const isExistingUser =
    userDetails?.user_id_hidden !== undefined &&
    userDetails?.user_id_hidden !== -1;

  // ---------- USER ----------
  if (isExistingUser) {
    payload.user_id_hidden = userDetails.user_id_hidden;

    if (userDetails.company?.value) {
      payload.company_id = userDetails.company.value;
    }

    // Add required fields even for existing users
    USER_REQUIRED_KEYS.forEach((key) => {
      if (key === "company") return; // already handled
      // skip image for existing users
      if (key === "user_profile_image") return;
      if (userDetails[key] !== undefined) {
        payload[payloadKeyMap[key]] = userDetails[key];
      }
    });
  } else {
    Object.entries(userDetails).forEach(([key, value]) => {
      if (!payloadKeyMap[key]) return;

      if (key === "company") {
        payload.company_id = value?.value ?? null;
        return;
      }

      if (key === "user_profile_image") {
        const val = userDetails[key];

        // URL → skip
        if (typeof val === "string") return;

        // new upload object
        if (val?.base64Data) {
          payload.user_profile_image = [val];
        }

        return;
      }

      payload[payloadKeyMap[key]] = value;
    });
  }

  // ---------- REQUEST ----------
  Object.entries(requestDetails).forEach(([key, value]) => {
    if (!payloadKeyMap[key]) return;

    let finalValue = value;

    if (key === "service_type") {
      const selectedOption = selectOptionsMap?.service_type.find(
        (opt) => opt.value === value
      );
      finalValue = selectedOption?.label || value;
    }

    if (key === "brand_info") {
      const selectedOption = selectOptionsMap?.brand_info.find(
        (opt) => opt.value === value
      );
      finalValue = selectedOption?.label || value;
    }

    if (key === "budget_type") {
      const selectedOption = selectOptionsMap?.budget_type.find(
        (opt) => opt.value === value
      );
      finalValue = selectedOption?.label || value;
    }

    if (key === "closed_via") {
      const selectedOption = selectOptionsMap?.closed_via.find(
        (opt) => opt.value === value
      );
      finalValue = selectedOption?.label || value;
    }

    if (key === "platform" && value === "Both") {
      finalValue = "Instagram,YouTube";
    }

    if (key === "is_high_value") {
      finalValue = value ? 1 : 0; // API expects 0 / 1
    }

    if (key === "any_language") {
      finalValue = value ? 1 : 0; // API expects 0 / 1
    }

    if (key === "barter") {
      finalValue = value ? 1 : 0; // API expects 0 / 1
    }

    payload[payloadKeyMap[key]] = finalValue;
  });

  return payload;
};

/* ---------- Campaign Wrapper Component ---------- */
const CampaignWrapper = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { actions } = useMarketplaceSlice();
  const { actions: validateAction } = useValidationSlice();
  const { actions: dropdownActions } = useDropdownSlice();
  const { campaignId } = useParams();

  const data = useSelector(selectMarketplaceUser);
  const { loading, updateLoading, statusUpdateLoading } =
    useSelector(selectMarketplace);
  const selectedMarketplace = useSelector(selectSelectedMarketplace);
  const { loading: createCampaignLoader } = useSelector(
    selectCreateMarketplaceCampaign
  );
  const {
    loading: validationLoading,
    success,
    error
  } = useSelector(triggerEmailValidation);

  // dropdown options
  const { data: categories, loading: categoriesLoading } =
    useSelector(selectCategories);
  const { data: cities, loading: citiesLoading } = useSelector(selectCities);
  const { data: languages, loading: languageLoading } =
    useSelector(selectLanguages);
  const { data: companies } = useSelector(selectCompanies);

  const user = data?.data?.user_detail;
  const errorUser = data?.error;

  // ----- Form states -----
  const [activeTab, setActiveTab] = useState(1);
  const [userDetails, setUserDetails] = useState(getEmptyUserDetails());
  const [hasCheckedUser, setHasCheckedUser] = useState(false);
  const [hasCheckedEmail, setHasCheckedEmail] = useState(false);
  const [hasRequestErrors, setHasRequestErrors] = useState(false);
  const [requestDetails, setRequestDetails] = useState({
    is_high_value: false,
    is_pan_india: false,
    creator_range_selector: [],
    is_budget_range: false,
    target_audience: "Agencies",
    platform: "",
    status: "approved"
  });

  const [approvalData, setApprovalData] = useState({
    collaboration_type: "",
    rejected_reason: ""
  });
  const [pendingApprovedLabel, setPendingApprovedLabel] = useState(null);
  const [pendingRejectedLabel, setPendingRejectedLabel] = useState(null);

  useEffect(() => {
    if (selectedMarketplace) {
      setApprovalData({
        collaboration_type: selectedMarketplace.collaboration_type || "",
        rejected_reason: selectedMarketplace.rejected_reason || ""
      });
    }
  }, [selectedMarketplace]);

  useEffect(() => {
    if (!statusUpdateLoading) {
      setPendingApprovedLabel(null);
      setPendingRejectedLabel(null);
    }
  }, [statusUpdateLoading]);

  // ----- Validation -----
  const isUserDetailsValid = useMemo(() => {
    const userId = userDetails?.user_id_hidden;
    if (!hasCheckedUser) return false;

    if (errorUser) return false;

    // If user exists and user_id_hidden is valid, allow
    if (userId !== -1) return true;

    if (!hasCheckedEmail) return false;

    // New user → validate required fields
    if (userId === -1) {
      // Email must be validated successfully
      if (!success) return false;

      return USER_REQUIRED_KEYS.every((key) => {
        const value = userDetails[key];

        if (key === "company") {
          return typeof value === "number" || value?.value;
        }

        return isValidField(value);
      });
    }

    return false;
  }, [userDetails, hasCheckedUser, hasCheckedEmail, success, errorUser]);

  const isRequestDetailsValid = useMemo(() => {
    return REQUEST_REQUIRED_KEYS.every((key) => {
      if (key === "target_audience") return true;
      if (key === "city_selector" && requestDetails.is_pan_india) return true;
      if (key === "language_selector" && requestDetails.any_language)
        return true;
      if (
        (key === "creator_count_min" || key === "creator_count_max") &&
        requestDetails.barter
      )
        return true;
      if (
        !requestDetails.is_budget_range &&
        (key === "budget_min" || key === "budget_max")
      )
        return true;
      if (requestDetails.is_budget_range && key === "budget") return true;
      if (
        ["any_language", "is_pan_india", "barter", "is_budget_range"].includes(
          key
        )
      )
        return true;
      return isValidField(requestDetails[key]);
    });
  }, [requestDetails]);

  // ----- Fetch dropdown options -----
  useEffect(() => {
    dispatch(dropdownActions.getCategoriesRequest());
    dispatch(dropdownActions.getLanguagesRequest());
    dispatch(dropdownActions.getCompaniesRequest());
    dispatch(dropdownActions.getCitiesRequest());
  }, []);

  // ----- Fetch campaign by ID if edit mode -----
  useEffect(() => {
    if (mode === "edit" && campaignId) {
      dispatch(actions.getMarketplaceById({ id: campaignId }));
    }
  }, [campaignId, mode]);

  // ----- Cleanup -----
  useEffect(() => {
    return () => {
      dispatch(actions.getMarketplaceByIdSuccess(null));
    };
  }, []);

  // ----- Go back -----
  const handleGoBack = () => {
    // reset local states
    setUserDetails(getEmptyUserDetails());
    setHasCheckedUser(false);
    setHasCheckedEmail(false);
    resetForm();

    // reset redux validation
    dispatch(validateAction.resetEmailValidation());

    navigate(`/marketplace${location.search}`);
  };

  // ----- Submit -----
  const handleCreateCampaign = () => {
    // Transform state into backend-ready payload
    const payload = transformPayload(userDetails, requestDetails);

    // Send the wrapped payload
    dispatch(
      actions.createMarketplaceCampaign({
        data: payload, // your action should POST body as is
        navigate,
        onSuccess: () => {
          resetForm();
          setUserDetails(getEmptyUserDetails());
          setHasCheckedUser(false);
          setHasCheckedEmail(false);
          dispatch(validateAction.resetEmailValidation());
        }
      })
    );
  };

  const resetForm = () => {
    setActiveTab(1);

    setHasCheckedUser(false);
    setHasCheckedEmail(false);

    setRequestDetails({
      is_high_value: false,
      is_pan_india: false,
      creator_range_selector: [],
      is_budget_range: false,
      target_audience: "Agencies",
      platform: "",
      status: "approved"
    });
  };

  const handleCategorySearch = (text) => {
    dispatch(
      dropdownActions.getCategoriesRequest({
        search: text
      })
    );
  };

  const handleLanguageSearch = (text) => {
    dispatch(dropdownActions.getLanguagesRequest({ search: text }));
  };

  const handleCompanySearch = (text) => {
    dispatch(dropdownActions.getCompaniesRequest({ search: text }));
  };

  const handleCitySearch = (text) => {
    dispatch(dropdownActions.getCitiesRequest({ search: text }));
  };

  const handleMarketplaceStatusUpdate = ({
    status,
    collaboration_type,
    reject_reason,
    type,
    high_value
  }) => {
    dispatch(
      actions.updateMarketplaceRequestStatus({
        marketplace_id_hash: selectedMarketplace?.id_hash,
        campaignId: selectedMarketplace?.id,
        status,
        collaboration_type,
        reject_reason,
        type,
        high_value
      })
    );
  };

  const approvedLabel =
    MARKETPLACE_APPROVE_OPTIONS.find(
      (item) =>
        item.type === selectedMarketplace?.type &&
        Number(item.high_value) === Number(selectedMarketplace?.is_high_value)
    )?.label || "Select Action";

  return (
    <CampaignContainer>
      <CompaniesWrapperStyles className="campaign-wrapper">
        <div className="go-back-wrapper">
          <div className="go-back-icon" onClick={handleGoBack}>
            <Icon component={ArrowLeftIcon} format="outline" weight="regular" />
          </div>
          <div className="update-text-wrapper">
            <h4 className="truncate-text">
              {mode === "edit" ? "Edit Campaign" : "Create Campaign"}
            </h4>
          </div>
        </div>

        {mode === "edit" &&
          (selectedMarketplace?.service_type === "Influencers" ||
            selectedMarketplace?.service_type === "influencers") && (
            <div className="filter-bar-menus">
              {selectedMarketplace ? (
                <>
                  {selectedMarketplace?.status !== "rejected" && (
                    <PillButtons
                      placeholderText={
                        !approvalData?.collaboration_type ? "Approved" : ""
                      }
                      statusPlaceholder="statusActive"
                      prefix="Approved as"
                      mainLabelText={
                        statusUpdateLoading && pendingApprovedLabel
                          ? pendingApprovedLabel
                          : COLLABORATION_TYPE.find(
                              (item) =>
                                item.value?.trim().toLowerCase() ===
                                selectedMarketplace?.collaboration_type
                                  ?.trim()
                                  .toLowerCase()
                            )?.label || ""
                      }
                      menuItems={COLLABORATION_TYPE}
                      onSelect={(item) => {
                        setPendingApprovedLabel(item.label);
                        handleMarketplaceStatusUpdate({
                          status: "approved",
                          collaboration_type: item.value
                        });
                      }}
                      size="M"
                      customClassName="filter-dropdown"
                      statusClassName="pill-approved"
                      rightIcon={
                        <Icon
                          component={CaretDownIcon}
                          format="outline"
                          weight="regular"
                        />
                      }
                      disabled={selectedMarketplace?.status === "approved"}
                      closeOnLoadComplete
                      loading={statusUpdateLoading}
                    />
                  )}

                  {selectedMarketplace?.status !== "approved" && (
                    <PillButtons
                      placeholderText={
                        !approvalData?.rejected_reason ? "Rejected" : ""
                      }
                      statusPlaceholder="statusActive"
                      prefix="Rejected - "
                      mainLabelText={
                        statusUpdateLoading && pendingRejectedLabel
                          ? pendingRejectedLabel
                          : rejectReasonOptions.find(
                              (item) =>
                                item.apiValue?.trim().toLowerCase() ===
                                selectedMarketplace?.rejected_reason
                                  ?.trim()
                                  .toLowerCase()
                            )?.label || ""
                      }
                      menuItems={rejectReasonOptions}
                      onSelect={(item) => {
                        setPendingRejectedLabel(item.label);
                        handleMarketplaceStatusUpdate({
                          status: "rejected",
                          reject_reason: item.value
                        });
                      }}
                      size="M"
                      customClassName="filter-dropdown"
                      statusClassName="pill-rejected"
                      rightIcon={
                        <Icon
                          component={CaretDownIcon}
                          format="outline"
                          weight="regular"
                        />
                      }
                      disabled={selectedMarketplace?.status === "rejected"}
                      closeOnLoadComplete
                      loading={statusUpdateLoading}
                    />
                  )}
                </>
              ) : null}
            </div>
          )}

        {mode === "edit" &&
          selectedMarketplace?.service_type?.toLowerCase() !==
            "influencers" && (
            <div className="filter-bar-menus">
              {selectedMarketplace ? (
                <>
                  {selectedMarketplace?.status !== "rejected" && (
                    <PillButtons
                      placeholderText={approvedLabel}
                      statusPlaceholder="statusActive"
                      prefix=""
                      mainLabelText={
                        statusUpdateLoading && pendingApprovedLabel
                          ? pendingApprovedLabel
                          : selectedMarketplace?.status === "approved"
                            ? "Approved"
                            : ""
                      }
                      menuItems={MARKETPLACE_APPROVE_OPTIONS}
                      onSelect={(item) => {
                        setPendingApprovedLabel(item.label);

                        handleMarketplaceStatusUpdate({
                          status: "approved",
                          type: item.type,
                          high_value: item.high_value
                        });
                      }}
                      size="M"
                      customClassName="filter-dropdown"
                      statusClassName="pill-approved"
                      rightIcon={
                        <Icon
                          component={CaretDownIcon}
                          format="outline"
                          weight="regular"
                        />
                      }
                      disabled={selectedMarketplace?.status === "approved"}
                      closeOnLoadComplete
                      loading={statusUpdateLoading}
                    />
                  )}

                  {selectedMarketplace?.status !== "approved" && (
                    <PillButtons
                      placeholderText={
                        pendingRejectedLabel ||
                        (selectedMarketplace?.status === "rejected"
                          ? "Rejected"
                          : "Select Action")
                      }
                      statusPlaceholder="statusActive"
                      prefix=""
                      mainLabelText={
                        statusUpdateLoading && pendingRejectedLabel
                          ? pendingRejectedLabel
                          : selectedMarketplace?.status === "rejected"
                            ? "Rejected"
                            : ""
                      }
                      menuItems={MARKETPLACE_REJECT_OPTIONS}
                      onSelect={(item) => {
                        setPendingRejectedLabel(item.label);

                        handleMarketplaceStatusUpdate({
                          status: "rejected"
                        });
                      }}
                      size="M"
                      customClassName="filter-dropdown"
                      statusClassName="pill-rejected"
                      rightIcon={
                        <Icon
                          component={CaretDownIcon}
                          format="outline"
                          weight="regular"
                        />
                      }
                      disabled={selectedMarketplace?.status === "rejected"}
                      closeOnLoadComplete
                      loading={statusUpdateLoading}
                    />
                  )}
                </>
              ) : null}
            </div>
          )}
      </CompaniesWrapperStyles>

      {mode === "edit" && (
        <div
          className={
            loading
              ? "campaign-middle-wrapper loading"
              : "campaign-middle-wrapper"
          }
        >
          {loading ? (
            <LoadingSpinner
              color="#797979"
              text="Loading Data..."
              fontSize={16}
            />
          ) : (
            <EditCampaign
              campaignState={selectedMarketplace}
              mode={mode}
              updateLoading={updateLoading}
              handleGoBack={handleGoBack}
              categories={categories}
              languages={languages}
              companies={companies}
              cities={cities}
              onCategorySearch={handleCategorySearch}
              onLanguageSearch={handleLanguageSearch}
              onCompanySearch={handleCompanySearch}
              onCitySearch={handleCitySearch}
              categoriesLoading={categoriesLoading}
              languagesLoading={languageLoading}
              citiesLoading={citiesLoading}
            />
          )}
          <div className="border-bg" />
        </div>
      )}

      {mode === "create" && (
        <div className="campaign-tabs-container">
          <div className="tabs-header">
            <div
              className={`tab ${activeTab === 1 ? "active" : ""}`}
              onClick={() => setActiveTab(1)}
            >
              <div
                className={`tab-circle ${activeTab === 2 ? "tab-circle-full" : ""}`}
              >
                {activeTab > 1 ? (
                  <Icon
                    component={CheckIcon}
                    size={12}
                    color={activeTab === 2 ? "#fff" : ""}
                  />
                ) : (
                  "1"
                )}
              </div>
              <span
                className={`details ${activeTab === 2 ? "user-details" : ""}`}
              >
                User Details
              </span>
            </div>

            <div className="progress-line-wrapper">
              <div
                className={`progress-line ${activeTab === 2 ? "full" : "half"}`}
              />
            </div>

            <div
              className={`tab ${activeTab === 2 ? "active" : ""} ${!isUserDetailsValid ? "disabled" : ""}`}
              onClick={() => {
                if (!isUserDetailsValid) return;
                setActiveTab(2);
              }}
            >
              <div className="tab-circle">2</div>
              <span className="details">Request Details</span>
            </div>
          </div>

          <div className="tab-content">
            {activeTab === 1 && (
              <>
                <UserDetails
                  user={user}
                  value={userDetails}
                  companies={companies}
                  onChange={setUserDetails}
                  setHasCheckedUser={setHasCheckedUser}
                  setHasCheckedEmail={setHasCheckedEmail}
                  validationLoading={validationLoading}
                  validateAction={validateAction}
                  success={success}
                  error={error}
                />
                <div className="button-wrappers">
                  <Button
                    size="M"
                    variant="secondary"
                    className="cancel-btn"
                    onClick={() => {
                      resetForm();
                      setUserDetails(getEmptyUserDetails());
                      dispatch(validateAction.resetEmailValidation());
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="M"
                    variant="primary"
                    className="next-btn"
                    disabled={!isUserDetailsValid}
                    onClick={() => setActiveTab(2)}
                  >
                    Next <Icon component={ArrowRightIcon} />
                  </Button>
                </div>
              </>
            )}
            {activeTab === 2 && (
              <>
                <RequestDetails
                  value={requestDetails}
                  onChange={setRequestDetails}
                  setHasRequestErrors={setHasRequestErrors}
                  categories={categories}
                  languages={languages}
                  companies={companies}
                  cities={cities}
                  onCategorySearch={handleCategorySearch}
                  onLanguageSearch={handleLanguageSearch}
                  onCompanySearch={handleCompanySearch}
                  onCitySearch={handleCitySearch}
                  categoriesLoading={categoriesLoading}
                  languagesLoading={languageLoading}
                  citiesLoading={citiesLoading}
                />
                <div className="button-wrappers previous-one">
                  <Button
                    variant="secondary"
                    size="M"
                    className="prev-btn"
                    onClick={() => setActiveTab(1)}
                  >
                    Previous
                  </Button>
                  <Button
                    size="M"
                    variant="primary"
                    className="create-btn"
                    disabled={
                      !isUserDetailsValid ||
                      !isRequestDetailsValid ||
                      hasRequestErrors
                    }
                    onClick={handleCreateCampaign}
                  >
                    {createCampaignLoader ? (
                      "Loading..."
                    ) : (
                      <>
                        Create Campaign <Icon component={ArrowRightIcon} />
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </CampaignContainer>
  );
};

export default CampaignWrapper;
